<template>
  <div class="contenedor-principal">
    <h1><strong>Free Tours</strong>: Descubre el entorno natural y geológico a tu ritmo</h1>

    <h2>Introducción: Una nueva forma de explorar</h2>
    <p>Para aquellos que aman la libertad de planificar sus viajes a su manera, las visitas turísticas tradicionales y
      rígidas pueden resultar restrictivas. Aunque en ciertos lugares con aforo limitado (como museos cerrados o grandes
      monumentos) es necesario adaptarse a horarios estrictos, en los últimos años ha surgido una gran alternativa en el
      mundo del turismo: los <strong>free tours</strong> o <strong>tours de libre pago</strong>. En este artículo
      exploraremos qué son, cómo funcionan y cómo estamos adaptando este modelo en Geobizi para acercarte a la
      naturaleza y la geología.</p>

    <h2>¿Qué es un Free Tour y cómo funciona?</h2>
    <p>Un <strong>Free Tour</strong> (o tour de libre pago) es un recorrido guiado por puntos de interés donde no se
      establece una tarifa fija inicial para los participantes. En cambio, al final del recorrido, cada persona decide
      qué importe económico entregar al guía en función de su grado de satisfacción, la calidad de las explicaciones y
      sus propias posibilidades económicas.</p>
    <p>La principal ventaja radica en su <strong>flexibilidad</strong> y en la motivación extra del guía. Aunque siempre
      recomendamos reservar con antelación para asegurar el control del aforo (especialmente en entornos naturales
      delicados), este formato es mucho más accesible para todos los públicos.</p>

    <h2>Ventajas y Desventajas de los Free Tours</h2>

    <h3>Ventajas:</h3>
    <ul>
      <li><strong>Accesibilidad Económica:</strong> Permiten a familias y viajeros disfrutar de experiencias
        divulgativas y enriquecedoras sin que el precio sea una barrera de entrada.</li>
      <li><strong>Conexión Auténtica:</strong> Los guías se esfuerzan al máximo en cada ruta. En nuestro caso, los
        educadores y geólogos transmitimos una pasión real por el entorno local.</li>
      <li><strong>Transparencia:</strong> Valoras la experiencia una vez la has vivido, no antes.</li>
    </ul>

    <h3>Desventajas (y cómo las evitamos en Geobizi):</h3>
    <ul>
      <li><strong>Masificación:</strong> Muchos free tours urbanos llevan a grupos de 50 personas. <em>(En Geobizi
          limitamos estrictamente nuestros grupos a 20-25 personas para no impactar en la naturaleza y asegurar que
          todos escuchen bien).</em></li>
      <li><strong>Calidad variable:</strong> A veces los guías no son expertos en la materia. <em>(Nuestras rutas están
          diseñadas y guiadas por profesionales del medioambiente y la geología).</em></li>
    </ul>

    <h2>Preguntas Frecuentes sobre el formato Free Tour</h2>

    <h3>1. ¿"Free" significa que es gratis?</h3>
    <p>Es un error común de traducción. "Free" en este contexto apela a la "libertad" (libre pago), no a que el trabajo
      del guía sea gratuito. El guía invierte su tiempo, conocimientos y seguros en la ruta, por lo que al final del
      recorrido se espera una aportación económica justa que valore ese esfuerzo.</p>

    <h3>2. ¿Cuánto debo dar al finalizar la ruta?</h3>
    <p>La cantidad es completamente libre. Por lo general, dependiendo de la duración de la ruta (suele ser de 1.5 a 2
      horas) y la calidad de la experiencia, las aportaciones suelen rondar entre los 10€ y 15€ por adulto, aunque cada
      familia aporta lo que considera oportuno.</p>

    <h3>3. ¿Tengo que reservar?</h3>
    <p>¡Sí! Especialmente en nuestras rutas medioambientales. Para proteger los espacios naturales que visitamos y
      cumplir con las normativas municipales, necesitamos saber exactamente cuántas personas asisten.</p>

    <div class="caja-recurso">
      <h3>Nuestros Free Tours de Naturaleza y Geología</h3>
      <p>
        En Geobizi hemos apostado por este formato para democratizar la divulgación científica. Organizamos
        <strong>rutas medioambientales, geológicas y mixtas</strong> en formato Free Tour (con grupos reducidos). Las
        fechas las vamos anunciando periódicamente. ¿Te vienes a explorar la costa y los bosques con nosotros?
      </p>
      <div class="botones-recurso">
        <a href="/calendario" class="proximas-actividades">Consultar Calendario</a>
        <a href="/contacto" class="btn-ver-mas">Contactar con nosotros</a>
      </div>
    </div>

    <p class="social-media"><b>¡Síguenos en redes sociales para ser el primero en enterarte de los nuevos Free
        Tours!</b></p>
    <div class="redes-sociales">
      <a href="https://www.instagram.com/geotxiki/" target="_blank" rel="noopener noreferrer"
        title="Geobizi en Instagram">
        <img src="@/assets/icons/instagram.png" alt="Geobizi en Instagram" class="social-icon" loading="lazy" />
      </a>
      <a href="https://www.facebook.com/geobizirik/" target="_blank" rel="noopener noreferrer"
        title="Geobizi en Facebook">
        <img src="@/assets/icons/facebook.png" alt="Geobizi en Facebook" class="social-icon" loading="lazy" />
      </a>
      <a href="https://www.tiktok.com/@geobizi" target="_blank" rel="noopener noreferrer" title="Geobizi en TikTok">
        <img src="@/assets/icons/tiktok.png" alt="Geobizi en TikTok" class="social-icon" loading="lazy" />
      </a>
    </div>

    <div class="imagen-trio">
      <div>
        <img class="img" src="/imagenes/blog/detalle/freetour1.avif"
          alt="Grupo de familias participando en un Free Tour de naturaleza" title="Free Tour en la naturaleza"
          loading="lazy" />
      </div>
      <div>
        <img class="img" src="/imagenes/blog/detalle/freetour2.avif"
          alt="Guía de Geobizi explicando la geología local durante un recorrido" title="Guía local experto"
          loading="lazy" />
      </div>
      <div>
        <img class="img" src="/imagenes/blog/detalle/freetour3.avif"
          alt="Participantes observando el entorno en una ruta medioambiental guiada" title="Ruta medioambiental"
          loading="lazy" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { useHead } from '@vueuse/head'

const pageUrl = 'https://www.geobizi.com/blog/free-tours'
const ogImage = 'https://www.geobizi.com/imagenes/blog/detalle/freetour1.avif'

useHead({
  title: '¿Qué es un Free Tour? Rutas de Naturaleza y Geología | Geobizi',
  meta: [
    { name: 'description', content: 'Descubre qué es y cómo funciona un Free Tour. Conoce las rutas guiadas de libre pago de Geobizi centradas en la geología y la naturaleza de Bizkaia.' },
    { name: 'robots', content: 'index, follow' },
    { name: 'author', content: 'Geobizi' },
    { name: 'theme-color', content: '#0b8a4c' },
    { name: 'language', content: 'es' },
    { property: 'og:title', content: 'Free Tours de Naturaleza: Qué son y cómo funcionan | Geobizi' },
    { property: 'og:description', content: 'Guía práctica sobre Free Tours: descubre las ventajas de las rutas de libre pago y cómo reservar nuestras salidas medioambientales.' },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: pageUrl },
    { property: 'og:image', content: ogImage },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:image', content: ogImage },
  ],
  link: [
    { rel: 'canonical', href: pageUrl },
    { rel: 'image_src', href: ogImage }
  ],
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": "Free Tours: Descubriendo la Naturaleza y Geología a tu Ritmo",
        "description": "Guía práctica sobre Free Tours: qué son, cómo funcionan y consejos para disfrutar de rutas medioambientales.",
        "image": ogImage,
        "author": {
          "@type": "Organization",
          "name": "Geobizi",
          "url": "https://www.geobizi.com"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Geobizi",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.geobizi.com/imagenes/GeobiziLogo.png"
          },
          // AÑADIDO TIKTOK AL SCHEMA PARA GOOGLE
          "sameAs": [
            "https://www.facebook.com/geobizirik/",
            "https://www.instagram.com/geotxiki/",
            "https://www.tiktok.com/@geobizi",
            "https://www.youtube.com/channel/UCw-C_J0y-jKHp7Zx92lsKfg"
          ]
        },
        "datePublished": "2024-06-15T08:00:00+01:00",
        "dateModified": "2026-04-14T10:00:00+02:00",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": pageUrl
        }
      })
    }
  ]
})
</script>

<style scoped>
.social-icon {
  width: 2.5rem;
  margin: 0.5rem 1rem 1.5rem 0;
  transition: transform 0.3s;
}

.social-icon:hover {
  transform: scale(1.1);
}

.social-media {
  margin-top: 2rem;
  color: var(--darkgrey);
}

.redes-sociales {
  margin-left: 1rem;
}

.caja-recurso {
  background-color: var(--megashoftgreen);
  border-left: 4px solid var(--shoftgreen);
  padding: 1.5rem;
  margin: 2.5rem 0;
  border-radius: 0 0.5rem 0.5rem 0;
}

.caja-recurso h3 {
  margin-top: 0;
  color: var(--lightgreen);
  font-size: 1.2rem;
}

.caja-recurso p {
  margin-bottom: 1.5rem;
  color: var(--darkgrey);
}

.botones-recurso {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.proximas-actividades {
  background-color: var(--supershoftgreen);
  color: var(--lightgreen);
  padding: 0.6rem 1.2rem;
  text-decoration: none;
  font-weight: bold;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.proximas-actividades:hover {
  background-color: var(--shoftgreen);
  color: var(--white);
}

.btn-ver-mas {
  background-color: transparent;
  color: var(--lightgreen);
  padding: 0.6rem 1.2rem;
  text-decoration: none;
  font-weight: bold;
  border: 1px solid var(--shoftgreen);
  border-radius: 4px;
  transition: all 0.3s;
}

.btn-ver-mas:hover {
  background-color: var(--shoftgreen);
  color: white;
}

/* Este apartado es para las listas con hojitas */

/* Quitar los puntos por defecto de la lista */
.contenedor-principal ul {
  list-style-type: none;
  padding-left: 0;
}

/* Dar espacio a la izquierda para que quepa el icono */
.contenedor-principal li {
  position: relative;
  padding-left: 2rem;
  /* Espacio para las hojitas */
  margin-bottom: 0.8rem;
  line-height: 1.5;
}

/* Insertar la imagen de las hojitas */
.contenedor-principal li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 2px;
  width: 1.2rem;
  height: 1.2rem;

  /* AQUÍ ESTÁ EL CAMBIO MÁGICO: subimos dos carpetas (../../) */
  background-image: url('../../assets/Hojitas.png');

  background-size: contain;
  background-repeat: no-repeat;
}
</style>